import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixIamAuthenticationV1Authentication from './iam-authentication-v1-authentication-authentication.http.js';
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

const TRANSFORM_PATHS_0 = [{ path: 'profile.addresses.address' }];
const TRANSFORM_PATHS_1 = [
  { path: 'identity.identityProfile.addresses.address' },
];

export interface Authentication {}

/**
 * Published for a passed check as well as a rejected one, so a consumer can tell a rejection rate
 * from a rejection count.
 *
 * The caller's IP, geo and user agent are deliberately not fields: they travel as call-scope aspects
 * in the message headers, which only reach a single-record subscription - a batch handler runs on an
 * orphan CallScope and loses them.
 */
export interface CaptchaVerification {
  /** @format GUID */
  eventId?: string;
  /** When the check happened, not when this was published. */
  eventTime?: Date | null;
  flow?: CaptchaFlowWithLiterals;
  identityType?: CaptchaIdentityTypeWithLiterals;
  /**
   * Legacy combined outcome. Use result and decision_reason instead.
   * @deprecated Legacy combined outcome. Use result and decision_reason instead.
   * @replacedBy result
   * @targetRemovalDate 2027-04-18
   */
  outcome?: CaptchaOutcomeWithLiterals;
  captchaKind?: CaptchaKindWithLiterals;
  /**
   * Legacy derived value. New events carry score and threshold instead.
   * @min -1
   * @max 1
   * @deprecated
   * @replacedBy score
   * @targetRemovalDate 2027-04-19
   */
  minScoreMargin?: number | null;
  /**
   * Google's reasons for the score: AUTOMATION, UNEXPECTED_ENVIRONMENT, TOO_MUCH_TRAFFIC, ...
   * @maxLength 100
   * @maxSize 100
   */
  reasons?: string[];
  /** What happened after verification, independently of why. */
  result?: CaptchaResultWithLiterals;
  /** The deciding cause of rejection or escalation. Unspecified for a pass. */
  decisionReason?: CaptchaDecisionReasonWithLiterals;
  /**
   * Raw reCAPTCHA assessment score, when available. Zero is a score, not an absent assessment.
   * @max 1
   */
  score?: number | null;
  /**
   * Effective minimum used for this assessment, including surface and OS policy.
   * Passing requires score strictly greater than threshold. Result/reason remain authoritative.
   * @max 1
   */
  threshold?: number | null;
}

export enum CaptchaFlow {
  REGISTER = 'REGISTER',
  /** Not published yet. */
  LOGIN = 'LOGIN',
}

/** @enumType */
export type CaptchaFlowWithLiterals = CaptchaFlow | 'REGISTER' | 'LOGIN';

export enum CaptchaIdentityType {
  USER = 'USER',
  /** Not published yet. */
  MEMBER = 'MEMBER',
}

/** @enumType */
export type CaptchaIdentityTypeWithLiterals =
  | CaptchaIdentityType
  | 'USER'
  | 'MEMBER';

export enum CaptchaOutcome {
  PASSED = 'PASSED',
  LOW_SCORE = 'LOW_SCORE',
  /** Rejected as inauthentic, which is independent of the score. */
  INVALID_TOKEN = 'INVALID_TOKEN',
  MISSING_TOKEN = 'MISSING_TOKEN',
  /**
   * Escalated to a visible challenge. PerimeterX can cause this while reCAPTCHA itself passed, so it
   * does not on its own mean the captcha scored badly - `reasons` distinguishes them.
   */
  VISIBLE_REQUIRED = 'VISIBLE_REQUIRED',
}

/** @enumType */
export type CaptchaOutcomeWithLiterals =
  | CaptchaOutcome
  | 'PASSED'
  | 'LOW_SCORE'
  | 'INVALID_TOKEN'
  | 'MISSING_TOKEN'
  | 'VISIBLE_REQUIRED';

export enum CaptchaKind {
  INVISIBLE = 'INVISIBLE',
  VISIBLE = 'VISIBLE',
  /** Verified against a site key the client registered itself (branded and headless apps). */
  CLIENT_SITE_KEY = 'CLIENT_SITE_KEY',
}

/** @enumType */
export type CaptchaKindWithLiterals =
  | CaptchaKind
  | 'INVISIBLE'
  | 'VISIBLE'
  | 'CLIENT_SITE_KEY';

export enum CaptchaResult {
  VERIFICATION_PASSED = 'VERIFICATION_PASSED',
  VERIFICATION_REJECTED = 'VERIFICATION_REJECTED',
  CHALLENGE_REQUIRED = 'CHALLENGE_REQUIRED',
}

/** @enumType */
export type CaptchaResultWithLiterals =
  | CaptchaResult
  | 'VERIFICATION_PASSED'
  | 'VERIFICATION_REJECTED'
  | 'CHALLENGE_REQUIRED';

export enum CaptchaDecisionReason {
  SCORE_BELOW_THRESHOLD = 'SCORE_BELOW_THRESHOLD',
  TOKEN_INVALID = 'TOKEN_INVALID',
  TOKEN_MISSING = 'TOKEN_MISSING',
  PERIMETERX_REJECTED = 'PERIMETERX_REJECTED',
}

/** @enumType */
export type CaptchaDecisionReasonWithLiterals =
  | CaptchaDecisionReason
  | 'SCORE_BELOW_THRESHOLD'
  | 'TOKEN_INVALID'
  | 'TOKEN_MISSING'
  | 'PERIMETERX_REJECTED';

export interface RegisterV2Request {
  /** Identifier of the registering member. */
  loginId: LoginId;
  /** Password of the registering member. */
  password?: string;
  /** Profile information of registering member. */
  profile?: IdentityProfile;
  /** CAPTCHA tokens, when CAPTCHA setting is on. */
  captchaTokens?: CaptchaToken[];
  /** Additional data that's relevant for the flow. */
  clientMetaData?: Record<string, any> | null;
  /**
   * The factor to register with: `PASSWORD`, the default, or `EMAIL`, which sends a code and returns
   * `REQUIRE_REGISTER_CHALLENGE` until the code is submitted to `Verify`.
   */
  factorType?: FactorTypeWithLiterals;
  /** The organization the user registers into. The new user's account is created as a sub-account of this organization. */
  organization?: Organization;
}

export interface LoginId extends LoginIdTypeOneOf {
  /**
   * Login email address.
   * @format EMAIL
   */
  email?: string;
}

/** @oneof */
export interface LoginIdTypeOneOf {
  /**
   * Login email address.
   * @format EMAIL
   */
  email?: string;
}

export interface IdentityProfile {
  /** Member's first name. */
  firstName?: string | null;
  /** Member's last name. */
  lastName?: string | null;
  /** Member's display nickname. */
  nickname?: string | null;
  /** URL to member's profile picture. */
  picture?: string | null;
  /**
   * Deprecated. Use `secondaryEmails` instead.
   * @deprecated Deprecated. Use `secondaryEmails` instead.
   * @replacedBy secondary_emails
   * @targetRemovalDate 2023-11-01
   */
  emails?: string[];
  /**
   * Deprecated. Use `phonesV2` instead.
   * @deprecated Deprecated. Use `phonesV2` instead.
   * @replacedBy phones_v2
   * @targetRemovalDate 2023-11-01
   */
  phones?: string[];
  /**
   * Labels associated with the member.
   * @maxSize 2000
   */
  labels?: string[];
  /**
   * Member's preferred language code.
   * @format LANGUAGE
   */
  language?: string | null;
  /** Member's profile visibility setting. */
  privacyStatus?: PrivacyStatusWithLiterals;
  /**
   * Any number of custom fields. [Custom fields](https://support.wix.com/en/article/adding-custom-fields-to-contacts)
   * are used to store additional information about your site or app's contacts.
   */
  customFields?: CustomField[];
  /** Additional email addresses for the member. */
  secondaryEmails?: SecondaryEmail[];
  /** Phone numbers associated with the member. */
  phonesV2?: Phone[];
  /** Physical addresses for the member. */
  addresses?: AddressWrapper[];
  /**
   * Company name.
   * @maxLength 1000
   */
  company?: string | null;
  /**
   * Position within company.
   * @maxLength 1000
   */
  position?: string | null;
  /**
   * Member's birth date in `YYYY-MM-DD` format.
   * @format LOCAL_DATE
   */
  birthdate?: string | null;
  /**
   * URL-friendly slug for the member's profile.
   * @maxLength 255
   */
  slug?: string | null;
  /** Whether the member consents to receive marketing communications. */
  subscription?: boolean | null;
  /**
   * Tax ID or VAT number for personal or corporate use.
   * @maxLength 1000
   */
  vatId?: string | null;
}

export enum PrivacyStatus {
  /** No privacy status defined. */
  UNDEFINED = 'UNDEFINED',
  /** Profile details are visible to other members. */
  PUBLIC = 'PUBLIC',
  /** Profile details are hidden from other members. */
  PRIVATE = 'PRIVATE',
}

/** @enumType */
export type PrivacyStatusWithLiterals =
  | PrivacyStatus
  | 'UNDEFINED'
  | 'PUBLIC'
  | 'PRIVATE';

export interface CustomField {
  /**
   * Custom field name matching an extended field key (without `custom.` prefix).
   *
   * Must match a key from [`List Extended Fields`](https://dev.wix.com/docs/rest/api-reference/contacts/extended-fields/list-extended-fields).
   */
  name?: string;
  /** Value for the custom field. */
  value?: V1CustomValue;
}

export interface V1CustomValue extends V1CustomValueValueOneOf {
  /** Text value. */
  strValue?: string;
  /** Numeric value. */
  numValue?: number;
  /** Date/time value. */
  dateValue?: Date | null;
  /** List of values. */
  listValue?: V1ListValue;
  /** Key-value pairs. */
  mapValue?: V1MapValue;
  /** Bool value */
  boolValue?: boolean;
}

/** @oneof */
export interface V1CustomValueValueOneOf {
  /** Text value. */
  strValue?: string;
  /** Numeric value. */
  numValue?: number;
  /** Date/time value. */
  dateValue?: Date | null;
  /** List of values. */
  listValue?: V1ListValue;
  /** Key-value pairs. */
  mapValue?: V1MapValue;
  /** Bool value */
  boolValue?: boolean;
}

export interface V1ListValue {
  /** List of custom values. */
  value?: V1CustomValue[];
}

export interface V1MapValue {
  /** Map of custom field values. */
  value?: Record<string, V1CustomValue>;
}

export interface SecondaryEmail {
  /**
   * Secondary email address.
   * @format EMAIL
   */
  email?: string;
  /** Email tag. */
  tag?: EmailTagWithLiterals;
}

export enum EmailTag {
  UNTAGGED = 'UNTAGGED',
  MAIN = 'MAIN',
  HOME = 'HOME',
  WORK = 'WORK',
}

/** @enumType */
export type EmailTagWithLiterals =
  | EmailTag
  | 'UNTAGGED'
  | 'MAIN'
  | 'HOME'
  | 'WORK';

export interface Phone {
  /**
   * Country code for the phone number.
   * @format COUNTRY
   */
  countryCode?: string | null;
  /**
   * Phone number without country code.
   * @format PHONE
   */
  phone?: string;
  /** Phone tag. */
  tag?: PhoneTagWithLiterals;
}

export enum PhoneTag {
  UNTAGGED = 'UNTAGGED',
  MAIN = 'MAIN',
  HOME = 'HOME',
  MOBILE = 'MOBILE',
  WORK = 'WORK',
  FAX = 'FAX',
}

/** @enumType */
export type PhoneTagWithLiterals =
  | PhoneTag
  | 'UNTAGGED'
  | 'MAIN'
  | 'HOME'
  | 'MOBILE'
  | 'WORK'
  | 'FAX';

export interface AddressWrapper {
  /** Physical address details. */
  address?: Address;
  /** Address tag. */
  tag?: AddressTagWithLiterals;
}

/** Physical address */
export interface Address {
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Main address line, usually street and number as free text. */
  addressLine1?: string | null;
  /** Free text providing more detailed address info. Usually contains apartment, suite, and floor. */
  addressLine2?: string | null;
  /**
   * A string containing the full address of this location.
   * @internal
   */
  formatted?: string | null;
}

export enum AddressTag {
  UNTAGGED = 'UNTAGGED',
  HOME = 'HOME',
  WORK = 'WORK',
  BILLING = 'BILLING',
  SHIPPING = 'SHIPPING',
}

/** @enumType */
export type AddressTagWithLiterals =
  | AddressTag
  | 'UNTAGGED'
  | 'HOME'
  | 'WORK'
  | 'BILLING'
  | 'SHIPPING';

export interface CaptchaToken extends CaptchaTokenTokenOneOf {
  /** @maxLength 5000 */
  Recaptcha?: string;
  /** @maxLength 5000 */
  InvisibleRecaptcha?: string;
  /** @maxLength 5000 */
  NoCaptcha?: string;
  /** Use this when the client has registered their own reCAPTCHA site key (e.g., branded apps, headless apps). */
  recaptchaForClientSiteKey?: RecaptchaForClientSiteKey;
}

/** @oneof */
export interface CaptchaTokenTokenOneOf {
  /** @maxLength 5000 */
  Recaptcha?: string;
  /** @maxLength 5000 */
  InvisibleRecaptcha?: string;
  /** @maxLength 5000 */
  NoCaptcha?: string;
  /** Use this when the client has registered their own reCAPTCHA site key (e.g., branded apps, headless apps). */
  recaptchaForClientSiteKey?: RecaptchaForClientSiteKey;
}

/** reCAPTCHA token with a client-provided site key ID. */
export interface RecaptchaForClientSiteKey {
  /**
   * The reCAPTCHA token obtained from the client-side reCAPTCHA widget.
   * @maxLength 5000
   */
  token?: string;
  /**
   * The site key ID registered in ReCAPTCHA service SDL for this client's reCAPTCHA configuration.
   * @format GUID
   */
  siteKeyId?: string;
}

export enum FactorType {
  /** Requires a password. */
  PASSWORD = 'PASSWORD',
  /** Requires a code sent via SMS. */
  SMS = 'SMS',
  /** Requires a code sent by phone call. */
  CALL = 'CALL',
  /** Requires a code sent by email. */
  EMAIL = 'EMAIL',
  /** Requires authentication via an authenticator app. */
  TOTP = 'TOTP',
  /** Requires authentication via a push notification. */
  PUSH = 'PUSH',
  /** Requires authentication via WebAuthn/passkey. */
  WEBAUTHN = 'WEBAUTHN',
  /** Requires a recovery code. */
  RECOVERY_CODE = 'RECOVERY_CODE',
}

/** @enumType */
export type FactorTypeWithLiterals =
  | FactorType
  | 'PASSWORD'
  | 'SMS'
  | 'CALL'
  | 'EMAIL'
  | 'TOTP'
  | 'PUSH'
  | 'WEBAUTHN'
  | 'RECOVERY_CODE';

export interface Organization {
  /**
   * ID of the organization. Required whenever `organization` is set.
   * @format GUID
   */
  _id?: string;
  /**
   * One-time registration invite code for this organization. Optional.
   * @maxLength 500
   */
  inviteCode?: string | null;
}

export interface StateMachineResponse
  extends StateMachineResponseStateDataOneOf {
  /** @internal */
  requireMfaData?: RequireMfaData;
  /**
   * User needs to solve an MFA challenge, such as providing the code that sent to them by SMS.
   * @internal
   */
  mfaChallengeData?: MfaChallengeData;
  /**
   * Identity must prove a first factor before the IdP connection is merged.
   * @internal
   */
  requireFirstFactorData?: RequireFirstFactorData;
  /** Current state of the login or registration process. */
  state?: StateTypeWithLiterals;
  /** Session token. If `state` is not `SUCCESS`, this field is undefined. */
  sessionToken?: string | null;
  /** Token that represents the current state of the login or registration process. */
  stateToken?: string | null;
  /** Identity of the current member. */
  identity?: Identity;
  /** Additional data relevant to the login or registration process. */
  additionalData?: Record<string, CustomValue>;
}

/** @oneof */
export interface StateMachineResponseStateDataOneOf {
  /** @internal */
  requireMfaData?: RequireMfaData;
  /**
   * User needs to solve an MFA challenge, such as providing the code that sent to them by SMS.
   * @internal
   */
  mfaChallengeData?: MfaChallengeData;
  /**
   * Identity must prove a first factor before the IdP connection is merged.
   * @internal
   */
  requireFirstFactorData?: RequireFirstFactorData;
}

export enum StateType {
  /** Initial unknown state. */
  UNKNOWN_STATE = 'UNKNOWN_STATE',
  /** Login completed successfully. */
  SUCCESS = 'SUCCESS',
  /** Member needs the owner to approve their registration. */
  REQUIRE_OWNER_APPROVAL = 'REQUIRE_OWNER_APPROVAL',
  /** Member needs to verify their email. */
  REQUIRE_EMAIL_VERIFICATION = 'REQUIRE_EMAIL_VERIFICATION',
  /** The `status` is not one that prevents the member logging in, meaning it's not `OFFLINE`, `BLOCKED`, or `DELETED`. */
  STATUS_CHECK = 'STATUS_CHECK',
  /**
   * Multi-factor authentication is required to complete login.
   * @internal
   */
  REQUIRE_MFA = 'REQUIRE_MFA',
  /**
   * Indicates that the member needs to verify themselves using the selected verication factor type.
   * @internal
   */
  MFA_CHALLENGE = 'MFA_CHALLENGE',
  /**
   * User needs to provide the first factor authentication (e.g., password, webAuthN).
   * @internal
   */
  REQUIRE_FIRST_FACTOR = 'REQUIRE_FIRST_FACTOR',
  /** Member must submit the profile-completion form to continue registration. */
  REQUIRE_PROFILE_COMPLETION = 'REQUIRE_PROFILE_COMPLETION',
  /**
   * Registering identity must prove their sign-up factor (e.g. an emailed code) before the identity is created.
   * @internal
   */
  REQUIRE_REGISTER_CHALLENGE = 'REQUIRE_REGISTER_CHALLENGE',
  /**
   * Identity logging in must enter the code sent to their email before the login continues.
   * @internal
   */
  REQUIRE_LOGIN_CHALLENGE = 'REQUIRE_LOGIN_CHALLENGE',
  /**
   * An IdP login matched several users; the login waits until the caller picks one of them.
   * @internal
   */
  REQUIRE_ORGANIZATION_SELECTION = 'REQUIRE_ORGANIZATION_SELECTION',
}

/** @enumType */
export type StateTypeWithLiterals =
  | StateType
  | 'UNKNOWN_STATE'
  | 'SUCCESS'
  | 'REQUIRE_OWNER_APPROVAL'
  | 'REQUIRE_EMAIL_VERIFICATION'
  | 'STATUS_CHECK'
  | 'REQUIRE_PROFILE_COMPLETION';

export interface Identity {
  /**
   * Identity ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the identity is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the identity.
   *
   * Ignored when creating an identity.
   */
  revision?: string | null;
  /**
   * Date and time the identity was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the identity was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** The identity configured connections to authenticate with. */
  connections?: Connection[];
  /** Identity profile. */
  identityProfile?: IdentityProfile;
  /**
   * Additional information about the identity that can impact user access.
   * This data cannot be set.
   */
  metadata?: Metadata;
  /** Identity email address. */
  email?: Email;
  /** Identity's current status. */
  status?: StatusV2;
  /**
   * Custom attributes.
   * @internal
   */
  customAttributes?: Record<string, any> | null;
  /**
   * Identity verification factors.
   * @maxSize 10
   * @readonly
   */
  factors?: Factor[];
}

export interface Connection extends ConnectionTypeOneOf {
  /** IDP connection. */
  idpConnection?: IdpConnection;
  /** Authenticator connection. */
  authenticatorConnection?: AuthenticatorConnection;
}

/** @oneof */
export interface ConnectionTypeOneOf {
  /** IDP connection. */
  idpConnection?: IdpConnection;
  /** Authenticator connection. */
  authenticatorConnection?: AuthenticatorConnection;
}

export interface IdpConnection {
  /**
   * IDP connection ID.
   * @format GUID
   */
  idpConnectionId?: string;
  /** IDP user ID. */
  idpUserId?: string;
}

export interface AuthenticatorConnection {
  /**
   * Authenticator connection ID.
   * @format GUID
   */
  authenticatorConnectionId?: string;
  /** Whether re-enrollment is required. */
  reEnrollmentRequired?: boolean;
}

export interface Metadata {
  /**
   * General tags. For example, `"isOwner"`.
   * @internal
   * @readonly
   */
  tags?: string[];
}

export interface Email {
  /**
   * Email address.
   * @format EMAIL
   */
  address?: string;
  /** Whether the email address is verified. */
  isVerified?: boolean;
}

export interface StatusV2 {
  /** Descriptive name of the identity status. */
  name?: StatusNameWithLiterals;
  /** Reasons for the current identity status. */
  reasons?: V1ReasonWithLiterals[];
}

export enum StatusName {
  /** Unknown status. This value isn't used. */
  UNKNOWN_STATUS = 'UNKNOWN_STATUS',
  /** Identity is pending verification, owner approval, or both. */
  PENDING = 'PENDING',
  /** Identity is active and can log in. */
  ACTIVE = 'ACTIVE',
  /** Identity is deleted. This identity can't log in. */
  DELETED = 'DELETED',
  /** Identity is blocked. This identity can't log in. */
  BLOCKED = 'BLOCKED',
  /** Identity is offline. This identity can't log in. */
  OFFLINE = 'OFFLINE',
}

/** @enumType */
export type StatusNameWithLiterals =
  | StatusName
  | 'UNKNOWN_STATUS'
  | 'PENDING'
  | 'ACTIVE'
  | 'DELETED'
  | 'BLOCKED'
  | 'OFFLINE';

export enum V1Reason {
  /** Unknown reason. This value isn't used. */
  UNKNOWN_REASON = 'UNKNOWN_REASON',
  /** The identity is waiting for admin approval. After the approval, the identity has an `ACTIVE` status. */
  PENDING_ADMIN_APPROVAL_REQUIRED = 'PENDING_ADMIN_APPROVAL_REQUIRED',
  /** The identity has to verify the email. After the verification, the identity has an `ACTIVE` status. */
  PENDING_EMAIL_VERIFICATION_REQUIRED = 'PENDING_EMAIL_VERIFICATION_REQUIRED',
  /** The identity must complete the site's profile-completion form. Other pending requirements may remain after completion. */
  PENDING_PROFILE_COMPLETION_REQUIRED = 'PENDING_PROFILE_COMPLETION_REQUIRED',
}

/** @enumType */
export type V1ReasonWithLiterals =
  | V1Reason
  | 'UNKNOWN_REASON'
  | 'PENDING_ADMIN_APPROVAL_REQUIRED'
  | 'PENDING_EMAIL_VERIFICATION_REQUIRED'
  | 'PENDING_PROFILE_COMPLETION_REQUIRED';

export interface Factor {
  /**
   * Factor ID.
   * @format GUID
   */
  factorId?: string;
  /** Factor type. */
  type?: FactorTypeWithLiterals;
  /** Factor status. */
  status?: StatusWithLiterals;
}

export enum Status {
  /** Factor requires activation. */
  INACTIVE = 'INACTIVE',
  /** Factor is active and can be used for authentication. */
  ACTIVE = 'ACTIVE',
  /** Factor is blocked and cannot be used for authentication. The user should reenroll the factor. */
  REQUIRE_REENROLL = 'REQUIRE_REENROLL',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'INACTIVE'
  | 'ACTIVE'
  | 'REQUIRE_REENROLL';

export interface CustomValue extends CustomValueValueOneOf {
  /** String value. */
  strValue?: string;
  /** Number value. */
  numValue?: number;
  /** Date value. */
  dateValue?: Date | null;
  /** List value. */
  listValue?: ListValue;
  /** Map value. */
  mapValue?: MapValue;
}

/** @oneof */
export interface CustomValueValueOneOf {
  /** String value. */
  strValue?: string;
  /** Number value. */
  numValue?: number;
  /** Date value. */
  dateValue?: Date | null;
  /** List value. */
  listValue?: ListValue;
  /** Map value. */
  mapValue?: MapValue;
}

export interface ListValue {
  /** Custom value. */
  value?: CustomValue[];
}

export interface MapValue {
  /** Mapped custom value. */
  value?: Record<string, CustomValue>;
}

export interface RequireMfaData {
  /**
   * The factors available for the user to perform the required MFA.
   * @maxSize 100
   */
  availableFactors?: V1Factor[];
  /** The reason the user is required to perform MFA. */
  reason?: MfaReasonWithLiterals;
}

export interface V1Factor {
  /** Type of verification factor. */
  factorType?: FactorTypeWithLiterals;
  /** Availability status for second factor */
  factorStatus?: FactorStatusWithLiterals;
}

export enum FactorStatus {
  ENABLED = 'ENABLED',
  REQUIRE_ACTIVATION = 'REQUIRE_ACTIVATION',
  REQUIRE_REENROLL = 'REQUIRE_REENROLL',
  ENABLED_BY_RULE = 'ENABLED_BY_RULE',
  DISABLED_BY_RULE = 'DISABLED_BY_RULE',
}

/** @enumType */
export type FactorStatusWithLiterals =
  | FactorStatus
  | 'ENABLED'
  | 'REQUIRE_ACTIVATION'
  | 'REQUIRE_REENROLL'
  | 'ENABLED_BY_RULE'
  | 'DISABLED_BY_RULE';

export enum MfaReason {
  /** Mfa is required due to user settings */
  USER_SETTINGS = 'USER_SETTINGS',
  /** Mfa is required due to high risk login */
  HIGH_RISK_LOGIN = 'HIGH_RISK_LOGIN',
}

/** @enumType */
export type MfaReasonWithLiterals =
  | MfaReason
  | 'USER_SETTINGS'
  | 'HIGH_RISK_LOGIN';

export interface MfaChallengeData {
  /** Type of verifation factor. */
  factorType?: FactorTypeWithLiterals;
  verificationChallengeData?: VerificationChallenge;
  /**
   * Factors types the user can choose from to verify themselves.
   * @maxSize 100
   */
  availableFactors?: V1Factor[];
  /** The reason the user is required for verification. */
  reason?: MfaReasonWithLiterals;
}

export interface VerificationChallenge
  extends VerificationChallengeFactorChallengeDataOneOf {
  pushData?: PushChallengeData;
  webauthnData?: WebAuthnChallengeData;
  /** @maxLength 200 */
  hint?: string | null;
}

/** @oneof */
export interface VerificationChallengeFactorChallengeDataOneOf {
  pushData?: PushChallengeData;
  webauthnData?: WebAuthnChallengeData;
}

export interface PushChallengeData {
  /**
   * This should be tied to a specific push notification.
   * It will be sent in subsequent requests to verify the factor.
   * @maxLength 36
   */
  transactionId?: string;
}

export interface WebAuthnChallengeData {
  /**
   * A unique identifier for this challenge that will be used to correlate
   * the authentication response with this challenge.
   * This should be a cryptographically random GUID to prevent guessing attacks.
   * @format GUID
   */
  challengeId?: string;
  /**
   * A PublicKeyCredentialRequestOptions JSON as defined in the WebAuthn spec (https://www.w3.org/TR/webauthn-3/#dictdef-publickeycredentialrequestoptions).
   * @maxLength 40000
   */
  publicKeyCredentialRequestOptionsJson?: string;
}

export interface RequireFirstFactorData {
  /**
   * Organization the identity is under.
   * @format GUID
   */
  organizationId?: string;
}

export interface ValidateEmailRequest {
  /** @maxLength 254 */
  email: string;
}

export interface ValidateEmailResponse {
  valid?: boolean;
  failure?: EmailValidationFailure;
  formatInvalid?: boolean;
}

export interface EmailValidationFailure {
  reason?: ReasonWithLiterals;
}

export enum Reason {
  UNKNOWN_REASON = 'UNKNOWN_REASON',
  EMAIL_ADDRESS_BOUNCED = 'EMAIL_ADDRESS_BOUNCED',
  EMAIL_DOMAIN_MISSING_DNS_RECORDS = 'EMAIL_DOMAIN_MISSING_DNS_RECORDS',
  EMAIL_DOMAIN_FORMAT_INVALID = 'EMAIL_DOMAIN_FORMAT_INVALID',
  EMAIL_DOMAIN_PERMANENTLY_BLACKLISTED = 'EMAIL_DOMAIN_PERMANENTLY_BLACKLISTED',
}

/** @enumType */
export type ReasonWithLiterals =
  | Reason
  | 'UNKNOWN_REASON'
  | 'EMAIL_ADDRESS_BOUNCED'
  | 'EMAIL_DOMAIN_MISSING_DNS_RECORDS'
  | 'EMAIL_DOMAIN_FORMAT_INVALID'
  | 'EMAIL_DOMAIN_PERMANENTLY_BLACKLISTED';

export interface LoginV2Request {
  /** Identifier of identity logging in. */
  loginId: LoginId;
  /** Password of the identity logging in. */
  password?: string | null;
  /** CAPTCHA tokens, when CAPTCHA setting is on. */
  captchaTokens?: CaptchaToken[];
  /** Additional data that's relevant for the flow. */
  clientMetaData?: Record<string, any> | null;
  /**
   * webAuthn data:
   * 1. When the login_id is an email:
   * 1.a. if password is provided, this field should not be provided.
   * 1.b. if password is not provided, this field should contain a webauthn credential to attempt login.
   * 2. When the login_id is empty, this field should contain a WebAuthn credential. password is not applicable in this case.
   * @internal
   */
  webauthn?: WebAuthnVerifyData;
  /**
   * State token from a login flow that was started beforehand.
   *
   * When provided, this request continues an in-progress login flow rather than starting a new one.
   * @maxLength 1000
   */
  stateToken?: string | null;
  /**
   * The factor to log in with. Unset, it's inferred from the fields present. `EMAIL` sends a code and returns
   * `REQUIRE_LOGIN_CHALLENGE` until the code is submitted to `Verify`.
   * @internal
   */
  factorType?: FactorTypeWithLiterals;
}

export interface WebAuthnVerifyData {
  /**
   * The challenge_id from the WebAuthnChallengeData that was returned in the previous
   * verification response. This will always be present when completing the WebAuthn authentication
   * ceremony with the credential data from the client.
   * @format GUID
   */
  challengeId?: string | null;
  /**
   * If this field is present, this is a request to complete the WebAuthn authentication
   * with the credential data from the client.
   *
   * The credential contains a JSON string representation of a WebAuthn PublicKeyCredential
   * object returned by the browser during authentication, serialized as per the WebAuthn specification.
   * The SPI implementation should use this to verify the authentication assertion.
   *
   * Any standards-compliant WebAuthn server library should be able to process this serialized format.
   * For details on the serialization format, refer to the W3C WebAuthn specification:
   * https://www.w3.org/TR/webauthn-3/#dom-publickeycredential-tojson
   * @maxLength 3000
   */
  credential?: string | null;
}

export interface ChangePasswordRequest {
  /**
   * New password to set for the logged-in member.
   *
   * The password must meet the site's configured password requirements for length and complexity.
   */
  newPassword: string;
}

export interface ChangePasswordResponse {}

export interface LoginWithIdpConnectionRequest {
  /**
   * The id of the connection id (can be fetched by calling connection-service.listEnabledConnectionsClientData
   * @format GUID
   */
  idpConnectionId?: string;
  /**
   * The id of the tenant the caller wants to login into
   * @format GUID
   */
  tenantId?: string;
  /** The type of the tenant the caller wants to login into */
  tenantType?: TenantTypeWithLiterals;
  customPayload?: Record<string, string>;
  /**
   * This flow ultimately returns an HTML page that asynchronously posts the LoginResponse via the BroadcastChannel API.
   * The message will be posted to a channel named `wix-idp-$session_id`, and encrypted with the `encryption_key`.
   * Encryption key should be base64 encoded. Encryption is done using AES-GCM with a random IV that's sent alongside the payload
   *
   * Optional at the proto level: required for the BroadcastChannel response mode only, and may be omitted
   * when `authorization_url` is provided (redirect response mode). Enforced in code.
   */
  sessionId?: string;
  /**
   * Base64-encoded encryption key for securing the authentication response.
   *
   * Used to encrypt the authentication response sent via BroadcastChannel API.
   * Encryption uses AES-GCM with a random IV.
   *
   * Optional at the proto level: required for the BroadcastChannel response mode only, and may be omitted
   * when `authorization_url` is provided (redirect response mode). Enforced in code.
   */
  encryptionKey?: string;
  /**
   * Visitor ID for tracking purposes.
   *
   * Optional identifier for analytics and tracking the authentication flow.
   */
  visitorId?: string | null;
  /**
   * Browser session identifier.
   *
   * Optional identifier for correlating the authentication request with browser session data.
   */
  bsi?: string | null;
  /**
   * Redirect response mode: when set, the SSO callback responds with a 302 to this URL carrying the
   * member session cookie (smSession) via Set-Cookie, instead of the BroadcastChannel HTML page.
   * Intended for OIDC-broker flows where Wix acts as the IdP: this URL is the outer authorization
   * endpoint to resume once the member session is established.
   * Must be on the same registrable domain as the host serving this login request (which is also the
   * domain the session cookie is set on). When set, `session_id` and `encryption_key` are not required.
   * @format WEB_URL
   */
  authorizationUrl?: string | null;
}

export enum TenantType {
  /** Unknown tenant type. This value isn't used. */
  UNKNOWN_TENANT_TYPE = 'UNKNOWN_TENANT_TYPE',
  /** The identity is a Wix user. */
  ACCOUNT = 'ACCOUNT',
  /** The identity is a member. */
  SITE = 'SITE',
  /**
   * Tenant is root.
   * @internal
   */
  ROOT = 'ROOT',
}

/** @enumType */
export type TenantTypeWithLiterals =
  | TenantType
  | 'UNKNOWN_TENANT_TYPE'
  | 'ACCOUNT'
  | 'SITE';

export interface RawHttpResponse {
  body?: Uint8Array;
  statusCode?: number | null;
  headers?: HeadersEntry[];
}

export interface HeadersEntry {
  key?: string;
  value?: string;
}

export interface RawHttpRequest {
  body?: Uint8Array;
  pathParams?: PathParametersEntry[];
  queryParams?: QueryParametersEntry[];
  headers?: HeadersEntry[];
  method?: string;
  rawPath?: string;
  rawQuery?: string;
}

export interface PathParametersEntry {
  key?: string;
  value?: string;
}

export interface QueryParametersEntry {
  key?: string;
  value?: string;
}

/**
 * Request for LoginWithIdpConnectionRedirect - OAuth redirect flow.
 * Uses redirect_uri/client_id instead of session_id/encryption_key.
 */
export interface LoginWithIdpConnectionRedirectRequest {
  /**
   * The id of the connection id (can be fetched by calling connection-service.listEnabledConnectionsClientData
   * @format GUID
   */
  idpConnectionId?: string;
  /**
   * The id of the tenant the caller wants to login into
   * @format GUID
   */
  tenantId?: string;
  /** The type of the tenant the caller wants to login into */
  tenantType?: TenantTypeWithLiterals;
  customPayload?: Record<string, string>;
  /**
   * OAuth app redirect URI for the callback.
   *
   * After successful authentication, the flow redirects to this URI with the `sessionToken`
   * as a query parameter. Must be a registered callback URL for the specified `client_id`.
   * @format WEB_URL
   */
  redirectUri?: string;
  /**
   * OAuth client ID for redirect flow validation.
   *
   * Used to validate that the redirect URI is a permitted callback URL for this OAuth client.
   * @format GUID
   */
  clientId?: string;
  /**
   * Visitor ID for tracking purposes.
   *
   * Optional identifier for analytics and tracking the authentication flow.
   */
  visitorId?: string | null;
  /**
   * Browser session identifier.
   *
   * Optional identifier for correlating the authentication request with browser session data.
   */
  bsi?: string | null;
}

export interface ConnectIdpRequest {
  /**
   * The id of the connection id (can be fetched by calling connection-service.listEnabledConnectionsClientData
   * @format GUID
   */
  idpConnectionId?: string;
  customPayload?: Record<string, string>;
  /**
   * This flow ultimately returns an HTML page that asynchronously posts the LoginResponse via the BroadcastChannel API.
   * The message will be posted to a channel named `wix-idp-$session_id`, and encrypted with the `encryption_key`.
   * Encryption key should be base64 encoded. Encryption is done using AES-GCM with a random IV that's sent alongside the payload
   */
  sessionId?: string;
  /**
   * Base64-encoded encryption key for securing the authentication response.
   *
   * Used to encrypt the authentication response sent via BroadcastChannel API.
   * Encryption uses AES-GCM with a random IV.
   */
  encryptionKey?: string;
}

export interface LoginCallbackRequest {
  /** state that that received on the redirect */
  state?: string;
  /** session token */
  sessionToken?: string;
}

export interface LoginWithIdpConnectionTokenParamsRequest {
  /**
   * The id of the connection id (can be fetched by calling connection-service.listEnabledConnectionsClientData)
   * @format GUID
   */
  idpConnectionId?: string;
  /** A set of fields that are required for the connection to be able to identify and authenticate the user */
  tokenParams?: Record<string, string>;
}

export interface SignOnRequest {
  /**
   * Member identifier for authentication.
   *
   * Currently only email addresses are supported.
   */
  loginId: LoginId;
  /**
   * Member profile information.
   *
   * Profile data such as first name, last name, and other member details.
   * If the member already exists, provided profile information will update the existing member's profile.
   * If the member doesn't exist, this profile information is used to create the new member.
   */
  profile?: IdentityProfile;
  /**
   * Whether to mark the member's email as verified without requiring verification.
   *
   * When `true`, marks the email as verified without requiring verification.
   * Use with caution in production as this bypasses security verification.
   *
   * Default: `false`
   */
  verifyEmail?: boolean;
  /**
   * Whether to merge with an existing contact with the same email address.
   *
   * When `true`, links the member to an existing contact record with the same email
   * and inherits contact data. When `false`, creates a new contact record.
   *
   * Default: `true`
   */
  mergeExistingContact?: boolean;
}

export interface SignOnResponse {
  /**
   * Session token for the authenticated member.
   *
   * Use this token to obtain access and refresh tokens for subsequent API calls.
   */
  sessionToken?: string;
  /**
   * Member identity information.
   *
   * Contains the member's identity details after successful authentication.
   */
  identity?: Identity;
}

/** logout request payload */
export interface LogoutRequest {
  /**
   * URL to redirect to after logout.
   *
   * Optional redirect destination after the logout process completes.
   * @format WEB_URL
   */
  postLogoutRedirectUri?: string | null;
  /**
   * Client identifier for the logout request.
   *
   * Identifies the application or client initiating the logout.
   * @format GUID
   */
  clientId?: string | null;
}

export interface VerifyRequest extends VerifyRequestFactorDataOneOf {
  /** SMS verification code sent to the member's phone number. */
  smsData?: SmsVerifyData;
  /** Voice call verification code received by the member. */
  callData?: CallVerifyData;
  /** Email verification code sent to the member's email address. */
  emailData?: ExternalEmailVerifyData;
  /** TOTP code from the member's authenticator app. */
  totpData?: TotpVerifyData;
  /** Push notification authentication response from the member's device. */
  pushData?: PushVerifyData;
  /** WebAuthn response from biometric or security key authentication. */
  webauthnData?: WebAuthnVerifyData;
  /** RecoveryCode response from a one-time code. */
  recoveryCodeData?: RecoveryCodeVerifyData;
  /**
   * State token from the initial authentication response.
   *
   * Required to continue the multi-step authentication process.
   * @maxLength 1000
   */
  stateToken?: string;
  /**
   * Type of authentication factor being verified.
   *
   * Specifies which verification method is being used (SMS, email, TOTP, etc.).
   */
  factorType?: FactorTypeWithLiterals;
  /**
   * Whether to remember this device for future authentication.
   *
   * When `true`, reduces future authentication requirements for this device.
   * Only applicable for second factor authentication.
   */
  rememberThisDevice?: boolean;
}

/** @oneof */
export interface VerifyRequestFactorDataOneOf {
  /** SMS verification code sent to the member's phone number. */
  smsData?: SmsVerifyData;
  /** Voice call verification code received by the member. */
  callData?: CallVerifyData;
  /** Email verification code sent to the member's email address. */
  emailData?: ExternalEmailVerifyData;
  /** TOTP code from the member's authenticator app. */
  totpData?: TotpVerifyData;
  /** Push notification authentication response from the member's device. */
  pushData?: PushVerifyData;
  /** WebAuthn response from biometric or security key authentication. */
  webauthnData?: WebAuthnVerifyData;
  /** RecoveryCode response from a one-time code. */
  recoveryCodeData?: RecoveryCodeVerifyData;
}

export interface SmsVerifyData {
  /**
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
  deliveryMethod?: DeliveryMethodWithLiterals;
  /**
   * The template ID to use for the SMS notification.
   * @minLength 1
   * @maxLength 100
   */
  templateId?: string | null;
}

export enum DeliveryMethod {
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
}

/** @enumType */
export type DeliveryMethodWithLiterals = DeliveryMethod | 'SMS' | 'WHATSAPP';

export interface CallVerifyData {
  /**
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
}

export interface ExternalEmailVerifyData {
  /**
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
}

export interface TotpVerifyData {
  /**
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
}

export interface PushVerifyData {
  /** @maxLength 36 */
  transactionId?: string | null;
}

export interface RecoveryCodeVerifyData {
  /**
   * @minLength 1
   * @maxLength 100
   */
  code?: string | null;
}

export interface SelectOrganizationRequest {
  /**
   * `state_token` from the `REQUIRE_ORGANIZATION_SELECTION` response.
   * @maxLength 1000
   */
  stateToken?: string;
  /**
   * The picked organization: one of the `organization_id`s in `require_organization_selection_data`.
   * @format GUID
   */
  organizationId?: string;
}

export interface SelectOrganizationResponse {
  /** The picked user's next login state, as `LoginV2` returns it. */
  stateMachineResponse?: StateMachineResponse;
}

export interface GetWebauthnChallengeRequest {
  /**
   * Identifier of the member requesting the WebAuthn challenge.
   *
   * If not provided, returns an anonymous WebAuthn challenge for usernameless authentication.
   */
  loginId?: LoginId;
  /**
   * ID of the organization whose account the member picked, when `login_id` is an email shared by several accounts.
   * The challenge is then issued for the email's account under this organization.
   * @format GUID
   */
  organizationId?: string | null;
}

export interface GetWebauthnChallengeResponse {
  /** The challenge that will be used for a WebAuthn authentication ceremony. */
  webauthn?: WebAuthnChallengeData;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type RegisterV2ApplicationErrors =
  | {
      code?: 'UNIMPLEMENTED_FEATURE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'THROTTLED_FEATURE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_CAPTCHA_TOKEN';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CAPTCHA_TOKEN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'IP_ADDRESS_FILTERED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'RECAPTCHA_FAILED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'RECAPTCHA_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MISSING_CAPTCHA_TOKEN';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'USER_EMAIL_OFAC_ADDRESS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'UNSUPPORTED_FIELD';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ATTRIBUTE_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CLIENT_METADATA_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_LD_SESSION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DUPLICATE_EMAIL';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'COULD_NOT_CREATE_SET_ACCOUNT_USER';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_USER_TYPE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'REGISTRATION_TO_EDITORX_NOT_ALLOWED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type RegisterV2ValidationErrors =
  | {
      ruleName?: 'PROVIDE_TENANT_ID';
    }
  | {
      ruleName?: 'INVALID_AUTHENTICATOR_ID';
    }
  | {
      ruleName?: 'VALUEֹֹ_TOO_SHORT';
    }
  | {
      ruleName?: 'VALUEֹֹ_TOO_LONG';
    }
  | {
      ruleName?: 'VALUE_DID_NOT_MATCH';
    };
/** @docsIgnore */
export type LoginV2ApplicationErrors =
  | {
      code?: 'THROTTLED_FEATURE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_CAPTCHA_TOKEN';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'CAPTCHA_TOKEN_REQUIRED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'REENROLLMENT_REQUIRED_BY_ADMIN';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TRUSTED_CLIENTS_ONLY';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'WIX_EMPLOYEE_PASSWORD_LOGIN_NOT_ALLOWED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type LoginV2ValidationErrors =
  | {
      ruleName?: 'PROVIDE_TENANT_ID';
    }
  | {
      ruleName?: 'INVALID_AUTHENTICATOR_ID';
    };

/**
 * Registers a new member.
 *
 * Typically, after a successful registration, you generate and use member tokens for the
 * registered member so that subsequent API calls are called as part of a member session.
 * Use the `sessionToken` in the response to [get the site member's access and refresh tokens](https://dev.wix.com/docs/go-headless/coding/rest-api/visitors-and-members/handle-members-with-custom-login#get-the-site-members-access-and-refresh-tokens).
 *
 * If the email used to register the member already exists as a contact email, the registering
 * member need to verify the email address using a code that is sent to the address.
 * @param loginId - Identifier of the registering member.
 * @public
 * @requiredField loginId
 * @fqn wix.iam.authentication.v1.AuthenticationService.RegisterV2
 */
export async function registerV2(
  loginId: LoginId,
  options?: RegisterV2Options
): Promise<
  NonNullablePaths<
    StateMachineResponse,
    | `state`
    | `identity.connections`
    | `identity.connections.${number}.idpConnection.idpConnectionId`
    | `identity.connections.${number}.idpConnection.idpUserId`
    | `identity.connections.${number}.authenticatorConnection.authenticatorConnectionId`
    | `identity.connections.${number}.authenticatorConnection.reEnrollmentRequired`
    | `identity.identityProfile.emails`
    | `identity.identityProfile.phones`
    | `identity.identityProfile.labels`
    | `identity.identityProfile.privacyStatus`
    | `identity.identityProfile.customFields`
    | `identity.identityProfile.customFields.${number}.name`
    | `identity.identityProfile.customFields.${number}.value.strValue`
    | `identity.identityProfile.customFields.${number}.value.numValue`
    | `identity.identityProfile.customFields.${number}.value.boolValue`
    | `identity.identityProfile.secondaryEmails`
    | `identity.identityProfile.secondaryEmails.${number}.email`
    | `identity.identityProfile.secondaryEmails.${number}.tag`
    | `identity.identityProfile.phonesV2`
    | `identity.identityProfile.phonesV2.${number}.phone`
    | `identity.identityProfile.phonesV2.${number}.tag`
    | `identity.identityProfile.addresses`
    | `identity.identityProfile.addresses.${number}.tag`
    | `identity.email.address`
    | `identity.email.isVerified`
    | `identity.status.name`
    | `identity.status.reasons`
    | `identity.factors`
    | `identity.factors.${number}.factorId`
    | `identity.factors.${number}.type`
    | `identity.factors.${number}.status`,
    5
  > & {
    __applicationErrorsType?: RegisterV2ApplicationErrors;
    __validationErrorsType?: RegisterV2ValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      loginId: loginId,
      password: options?.password,
      profile: options?.profile,
      captchaTokens: options?.captchaTokens,
      clientMetaData: options?.clientMetaData,
      factorType: options?.factorType,
      organization: options?.organization,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: TRANSFORM_PATHS_0,
      },
    ]
  );

  const reqOpts =
    ambassadorWixIamAuthenticationV1Authentication.registerV2(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: TRANSFORM_PATHS_1,
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          loginId: '$[0]',
          password: '$[1].password',
          profile: '$[1].profile',
          captchaTokens: '$[1].captchaTokens',
          clientMetaData: '$[1].clientMetaData',
          factorType: '$[1].factorType',
          organization: '$[1].organization',
        },
        singleArgumentUnchanged: false,
      },
      ['loginId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RegisterV2Options {
  /** Password of the registering member. */
  password?: string;
  /** Profile information of registering member. */
  profile?: IdentityProfile;
  /** CAPTCHA tokens, when CAPTCHA setting is on. */
  captchaTokens?: CaptchaToken[];
  /** Additional data that's relevant for the flow. */
  clientMetaData?: Record<string, any> | null;
  /**
   * The factor to register with: `PASSWORD`, the default, or `EMAIL`, which sends a code and returns
   * `REQUIRE_REGISTER_CHALLENGE` until the code is submitted to `Verify`.
   */
  factorType?: FactorTypeWithLiterals;
  /** The organization the user registers into. The new user's account is created as a sub-account of this organization. */
  organization?: Organization;
}

/** @public
 * @requiredField email
 * @fqn wix.iam.authentication.v1.AuthenticationService.ValidateEmail
 */
export async function validateEmail(
  email: string
): Promise<
  NonNullablePaths<
    ValidateEmailResponse,
    `valid` | `failure.reason` | `formatInvalid`,
    1
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ email: email });

  const reqOpts =
    ambassadorWixIamAuthenticationV1Authentication.validateEmail(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { email: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['email']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Logs in an existing member.
 *
 * Typically, after a successful login, you generate and use member tokens for the
 * logged-in member so that subsequent API calls are called as part of a member session.
 * Use the `sessionToken` in the response to [get the site member's access and refresh tokens](https://dev.wix.com/docs/go-headless/coding/rest-api/visitors-and-members/handle-members-with-custom-login#get-the-site-members-access-and-refresh-tokens).
 * @param loginId - Identifier of identity logging in.
 * @public
 * @requiredField loginId
 * @fqn wix.iam.authentication.v1.AuthenticationService.LoginV2
 */
export async function loginV2(
  loginId: LoginId,
  options?: LoginV2Options
): Promise<
  NonNullablePaths<
    StateMachineResponse,
    | `state`
    | `identity.connections`
    | `identity.connections.${number}.idpConnection.idpConnectionId`
    | `identity.connections.${number}.idpConnection.idpUserId`
    | `identity.connections.${number}.authenticatorConnection.authenticatorConnectionId`
    | `identity.connections.${number}.authenticatorConnection.reEnrollmentRequired`
    | `identity.identityProfile.emails`
    | `identity.identityProfile.phones`
    | `identity.identityProfile.labels`
    | `identity.identityProfile.privacyStatus`
    | `identity.identityProfile.customFields`
    | `identity.identityProfile.customFields.${number}.name`
    | `identity.identityProfile.customFields.${number}.value.strValue`
    | `identity.identityProfile.customFields.${number}.value.numValue`
    | `identity.identityProfile.customFields.${number}.value.boolValue`
    | `identity.identityProfile.secondaryEmails`
    | `identity.identityProfile.secondaryEmails.${number}.email`
    | `identity.identityProfile.secondaryEmails.${number}.tag`
    | `identity.identityProfile.phonesV2`
    | `identity.identityProfile.phonesV2.${number}.phone`
    | `identity.identityProfile.phonesV2.${number}.tag`
    | `identity.identityProfile.addresses`
    | `identity.identityProfile.addresses.${number}.tag`
    | `identity.email.address`
    | `identity.email.isVerified`
    | `identity.status.name`
    | `identity.status.reasons`
    | `identity.factors`
    | `identity.factors.${number}.factorId`
    | `identity.factors.${number}.type`
    | `identity.factors.${number}.status`,
    5
  > & {
    __applicationErrorsType?: LoginV2ApplicationErrors;
    __validationErrorsType?: LoginV2ValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    loginId: loginId,
    password: options?.password,
    captchaTokens: options?.captchaTokens,
    clientMetaData: options?.clientMetaData,
    stateToken: options?.stateToken,
  });

  const reqOpts =
    ambassadorWixIamAuthenticationV1Authentication.loginV2(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: TRANSFORM_PATHS_1,
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          loginId: '$[0]',
          password: '$[1].password',
          captchaTokens: '$[1].captchaTokens',
          clientMetaData: '$[1].clientMetaData',
          stateToken: '$[1].stateToken',
        },
        singleArgumentUnchanged: false,
      },
      ['loginId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface LoginV2Options {
  /** Password of the identity logging in. */
  password?: string | null;
  /** CAPTCHA tokens, when CAPTCHA setting is on. */
  captchaTokens?: CaptchaToken[];
  /** Additional data that's relevant for the flow. */
  clientMetaData?: Record<string, any> | null;
  /**
   * State token from a login flow that was started beforehand.
   *
   * When provided, this request continues an in-progress login flow rather than starting a new one.
   * @maxLength 1000
   */
  stateToken?: string | null;
}

/**
 * Changes the password of a logged-in member. The member must have a valid session to use this method.
 *
 * The member's session remains valid after password change.
 *
 * The new password must meet the site's configured password requirements for length
 * and complexity. These requirements are enforced automatically when processing the request.
 * @param newPassword - New password to set for the logged-in member.
 *
 * The password must meet the site's configured password requirements for length and complexity.
 * @public
 * @documentationMaturity preview
 * @requiredField newPassword
 * @permissionId IAM.CHANGE_PASSWORD
 * @fqn wix.iam.authentication.v1.AuthenticationService.ChangePassword
 */
export async function changePassword(newPassword: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    newPassword: newPassword,
  });

  const reqOpts =
    ambassadorWixIamAuthenticationV1Authentication.changePassword(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { newPassword: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['newPassword']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Authenticates a member without credentials and returns a session token.
 *
 * This method provides authentication that automatically creates or updates
 * member accounts as needed during the authentication process.
 * Use this only for trusted integrations and scenarios where you already have verified user information.
 * @param loginId - Member identifier for authentication.
 *
 * Currently only email addresses are supported.
 * @public
 * @documentationMaturity preview
 * @requiredField loginId
 * @permissionId IAM.AUTHENTICAITON_SIGN_ON
 * @applicableIdentity APP
 * @fqn wix.iam.authentication.v1.AuthenticationService.SignOn
 */
export async function signOn(
  loginId: LoginId,
  options?: SignOnOptions
): Promise<
  NonNullablePaths<
    SignOnResponse,
    | `sessionToken`
    | `identity.connections`
    | `identity.connections.${number}.idpConnection.idpConnectionId`
    | `identity.connections.${number}.idpConnection.idpUserId`
    | `identity.connections.${number}.authenticatorConnection.authenticatorConnectionId`
    | `identity.connections.${number}.authenticatorConnection.reEnrollmentRequired`
    | `identity.identityProfile.emails`
    | `identity.identityProfile.phones`
    | `identity.identityProfile.labels`
    | `identity.identityProfile.privacyStatus`
    | `identity.identityProfile.customFields`
    | `identity.identityProfile.customFields.${number}.name`
    | `identity.identityProfile.customFields.${number}.value.strValue`
    | `identity.identityProfile.customFields.${number}.value.numValue`
    | `identity.identityProfile.customFields.${number}.value.boolValue`
    | `identity.identityProfile.secondaryEmails`
    | `identity.identityProfile.secondaryEmails.${number}.email`
    | `identity.identityProfile.secondaryEmails.${number}.tag`
    | `identity.identityProfile.phonesV2`
    | `identity.identityProfile.phonesV2.${number}.phone`
    | `identity.identityProfile.phonesV2.${number}.tag`
    | `identity.identityProfile.addresses`
    | `identity.identityProfile.addresses.${number}.tag`
    | `identity.email.address`
    | `identity.email.isVerified`
    | `identity.status.name`
    | `identity.status.reasons`
    | `identity.factors`
    | `identity.factors.${number}.factorId`
    | `identity.factors.${number}.type`
    | `identity.factors.${number}.status`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      loginId: loginId,
      profile: options?.profile,
      verifyEmail: options?.verifyEmail,
      mergeExistingContact: options?.mergeExistingContact,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: TRANSFORM_PATHS_0,
      },
    ]
  );

  const reqOpts =
    ambassadorWixIamAuthenticationV1Authentication.signOn(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: TRANSFORM_PATHS_1,
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          loginId: '$[0]',
          profile: '$[1].profile',
          verifyEmail: '$[1].verifyEmail',
          mergeExistingContact: '$[1].mergeExistingContact',
        },
        singleArgumentUnchanged: false,
      },
      ['loginId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SignOnOptions {
  /**
   * Member profile information.
   *
   * Profile data such as first name, last name, and other member details.
   * If the member already exists, provided profile information will update the existing member's profile.
   * If the member doesn't exist, this profile information is used to create the new member.
   */
  profile?: IdentityProfile;
  /**
   * Whether to mark the member's email as verified without requiring verification.
   *
   * When `true`, marks the email as verified without requiring verification.
   * Use with caution in production as this bypasses security verification.
   *
   * Default: `false`
   */
  verifyEmail?: boolean;
  /**
   * Whether to merge with an existing contact with the same email address.
   *
   * When `true`, links the member to an existing contact record with the same email
   * and inherits contact data. When `false`, creates a new contact record.
   *
   * Default: `true`
   */
  mergeExistingContact?: boolean;
}

/**
 * Terminates the member's session and clears authentication cookies.
 *
 * Returns the following:
 * - `body`: HTML content for the logout response
 * - `statusCode`: HTTP status code (200 for success, 302 for redirect)
 * - `headers`: Array of key-value pairs including:
 * - `Set-Cookie` headers to remove all session cookies
 * - `Location` header (when `postLogoutRedirectUri` is provided, with 302 status)
 * @public
 * @documentationMaturity preview
 * @fqn wix.iam.authentication.v1.AuthenticationService.Logout
 */
export async function logout(
  options?: LogoutOptions
): Promise<
  NonNullablePaths<
    RawHttpResponse,
    `body` | `headers` | `headers.${number}.key` | `headers.${number}.value`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    postLogoutRedirectUri: options?.postLogoutRedirectUri,
    clientId: options?.clientId,
  });

  const reqOpts =
    ambassadorWixIamAuthenticationV1Authentication.logout(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          postLogoutRedirectUri: '$[0].postLogoutRedirectUri',
          clientId: '$[0].clientId',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface LogoutOptions {
  /**
   * URL to redirect to after logout.
   *
   * Optional redirect destination after the logout process completes.
   * @format WEB_URL
   */
  postLogoutRedirectUri?: string | null;
  /**
   * Client identifier for the logout request.
   *
   * Identifies the application or client initiating the logout.
   * @format GUID
   */
  clientId?: string | null;
}
